import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import clsx from 'clsx'
import ActionsMenu, { ActionsMenuItem } from 'components/ActionsMenu/ActionsMenu'
import { ChooseActionsLabel } from 'components/ChooseActionsLabel/ChooseActionsLabel'
import Dropdown, { IOption } from 'components/Dropdown/Dropdown'
import { useIsMobile } from 'hooks/useIsMobile'
import { ReactComponent as DebrisDumpSvg } from 'images/icons/debris_dump.svg'
import { ReactComponent as RemoveSvg } from 'images/icons/delete.svg'
import { ReactComponent as HeadlightOffSvg } from 'images/icons/headlight_off.svg'
import { ReactComponent as HeadlightOnSvg } from 'images/icons/headlight_on.svg'
import { ReactComponent as LocationSvg } from 'images/icons/location.svg'
import { ReactComponent as MappingSvg } from 'images/icons/mapping.svg'
import { ReactComponent as ParkingSvg } from 'images/icons/parking.svg'
import { ReactComponent as ChargingSvg } from 'images/icons/plug_in.svg'
import { ReactComponent as RefillingSvg } from 'images/icons/refilling.svg'
import { ReactComponent as SandClockSvg } from 'images/icons/sand_clock.svg'
import { ReactComponent as SatelliteSvg } from 'images/icons/satellite.svg'
import { ReactComponent as WashingSvg } from 'images/icons/washing.svg'
import { ReactComponent as WasteWaterDrainSvg } from 'images/icons/waste_water_drain.svg'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { PATH } from 'utils/constants'
import getTestId from 'utils/getTestId'
import { IMissionInfo } from 'utils/interfaces'
import { ACTION_TYPE_LABELS, ActionType } from 'utils/servicePointsCatalog'

import style from './MissionsTable.module.css'
import notCalculatedStyle from 'styles/missionRowNotCalculated.module.css'

export type TMissionTableRow = {
  mission: IMissionInfo
  onClick: () => void
  highlighted?: boolean
  onExclude?: () => void
  onMoveUp?: () => void
  onMoveDown?: () => void
  isFirst?: boolean
  isLast?: boolean
  location?: string | null
  checked?: boolean
  onCheckboxChange?: (checked: boolean) => void
  typeMismatch?: boolean
  equipmentSetup?: { front_lights?: boolean }
  onEquipmentSetupChange?: (setup: { front_lights: boolean }) => void
  index: number
  onDragStart?: (e: React.DragEvent<HTMLDivElement>, index: number) => void
  onDragOver?: (e: React.DragEvent<HTMLDivElement>, index: number) => void
  onDrop?: (e: React.DragEvent<HTMLDivElement>, index: number) => void
  onDragEnd?: (e: React.DragEvent<HTMLDivElement>) => void
  isDragging?: boolean
  hideActions?: boolean
  hideDragging?: boolean
  dragIndicator?: 'top' | 'bottom' | 'none'
  showEquipment?: boolean
  showLocalization?: boolean
  hideIdColumn?: boolean
}

export const MissionTableRow = ({
  mission,
  onClick,
  highlighted,
  onExclude,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  location,
  checked,
  onCheckboxChange,
  typeMismatch,
  equipmentSetup,
  onEquipmentSetupChange,
  index,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  isDragging = false,
  hideActions = false,
  hideDragging = false,
  dragIndicator = 'none',
  showEquipment = true,
  showLocalization = true,
  hideIdColumn = false,
}: TMissionTableRow) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const goToMission = () => {
    navigate(`${PATH.MISSION}${mission.id}`)
  }

  const isMobile = useIsMobile()

  // Adjust grid based on visible columns
  const gridStyle = {
    '--missions-table-grid': isMobile
      ? `${hideDragging ? '' : '5px '}${hideIdColumn ? '' : '40px '}1fr ${showLocalization ? '50px ' : ''}${
          showEquipment ? '35px ' : ''
        }35px${hideActions ? '' : ' 35px'}`
      : `${hideDragging ? '' : '5px '}${hideIdColumn ? '' : '48px '}3fr ${showLocalization ? '1.5fr ' : ''}${
          showEquipment ? '1.5fr ' : ''
        }0.5fr${hideActions ? '' : ' 22px'}`,
  } as React.CSSProperties

  return (
    <div
      draggable={!hideDragging}
      onDragStart={e => onDragStart?.(e, index)}
      onDragOver={e => onDragOver?.(e, index)}
      onDrop={e => onDrop?.(e, index)}
      onDragEnd={onDragEnd}
      className={clsx(
        style.missionRow,
        style.missionHighlightRow,
        highlighted && style.highlighted,
        isDragging && style.isDragging,
        dragIndicator === 'top' && style.dragIndicatorTop,
        dragIndicator === 'bottom' && style.dragIndicatorBottom,
        {
          [notCalculatedStyle.missionRowNotCalculated]: mission?.status !== 'ok' || typeMismatch,
        }
      )}
      style={gridStyle}
      title={
        typeMismatch
          ? 'Missions have different robot types'
          : mission?.status && mission.status !== 'ok'
          ? 'Mission is not calculated (status is not OK)'
          : undefined
      }
      onClick={onClick}
    >
      {!hideDragging && (
        <div className={style.headerColCentered} style={{ cursor: 'grab' }}>
          <span></span>⠿
        </div>
      )}
      {!hideIdColumn && <div className={style.headerCol}>{mission.id}</div>}
      <div className={style.colLeftMultiline}>{mission.name}</div>
      {showLocalization && (
        <div className={style.headerColCentered}>
          {isMobile ? (
            location === null || location === undefined ? (
              <SatelliteSvg width={18} height={18} className={style.themedIcon} />
            ) : (
              <MappingSvg width={18} height={18} className={style.themedIcon} />
            )
          ) : location === null || location === undefined ? (
            'GNSS'
          ) : (
            'SLAM'
          )}
        </div>
      )}
      {showEquipment && (
        <div className={style.headerColCentered} onClick={e => e.stopPropagation()}>
          <div
            onClick={() => onEquipmentSetupChange?.({ front_lights: !equipmentSetup?.front_lights })}
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            {equipmentSetup?.front_lights ? (
              <HeadlightOnSvg width={20} height={20} className={style.themedIcon} data-active={true} />
            ) : (
              <HeadlightOffSvg width={20} height={20} className={style.themedIcon} />
            )}
          </div>
        </div>
      )}
      <div className={style.headerColCentered} onClick={e => e.stopPropagation()}>
        <div
          role="button"
          tabIndex={0}
          aria-pressed={Boolean(checked)}
          aria-label={checked ? t('missions.hide_on_map', 'Hide on map') : t('missions.show_on_map', 'Show on map')}
          title={checked ? t('missions.hide_on_map', 'Hide on map') : t('missions.show_on_map', 'Show on map')}
          onClick={() => onCheckboxChange?.(!checked)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onCheckboxChange?.(!checked)
            }
          }}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          {checked ? (
            <VisibilityIcon fontSize="small" className={style.themedIcon} data-active={true} />
          ) : (
            <VisibilityOutlinedIcon fontSize="small" className={style.themedIcon} />
          )}
        </div>
      </div>
      {!hideActions && (
        <div className={style.headerCol} onClick={e => e.stopPropagation()}>
          <ActionsMenu>
            {!isFirst && onMoveUp && (
              <ActionsMenuItem onClick={onMoveUp} data-test-id={getTestId('move-up-mission')}>
                <span className={style.menuItemIcon} />
                <span className={style.menuItemText}>Move Up</span>
              </ActionsMenuItem>
            )}
            {!isLast && onMoveDown && (
              <ActionsMenuItem onClick={onMoveDown} data-test-id={getTestId('move-down-mission')}>
                <span className={style.menuItemIcon} />
                <span className={style.menuItemText}>Move Down</span>
              </ActionsMenuItem>
            )}
            <ActionsMenuItem onClick={goToMission} data-test-id={getTestId('edit-mission')}>
              <span className={style.menuItemIcon} />
              <span className={style.menuItemText}>{t('common.edit')}</span>
            </ActionsMenuItem>
            {onExclude && (
              <ActionsMenuItem onClick={onExclude} data-test-id={getTestId('exclude-mission')}>
                <RemoveSvg className={style.menuItemIcon} />
                <span className={style.menuItemText}>{t('common.exclude')}</span>
              </ActionsMenuItem>
            )}
          </ActionsMenu>
        </div>
      )}
    </div>
  )
}

export type TMaintenanceItem = {
  type: string
  icon: React.FunctionComponent<React.SVGProps<SVGSVGElement> & { title?: string | undefined }>
  hideDragging?: boolean
  hideActions?: boolean
  showEquipment?: boolean
  showLocalization?: boolean
  hideIdColumn?: boolean
  onDelete?: () => void
  onDragStart?: (e: React.DragEvent<HTMLDivElement>) => void
  onDragOver?: (e: React.DragEvent<HTMLDivElement>) => void
  onDrop?: (e: React.DragEvent<HTMLDivElement>) => void
  onDragEnd?: (e: React.DragEvent<HTMLDivElement>) => void
  isDragging?: boolean
  isFloating?: boolean
  description?: string
  time?: number
  onTimeChange?: (time: number) => void
  onEdit?: () => void
  dragIndicator?: 'top' | 'bottom' | 'none'
  allowedActions?: ActionType[]
  /** Show action chips when only one action remains (REFILLING lot mid-scenario). */
  allowSingleActionChips?: boolean
  selectedActions?: ActionType[]
  onActionToggle?: (action: ActionType) => void
  onMoveUp?: () => void
  onMoveDown?: () => void
  isFirst?: boolean
  isLast?: boolean
  onClick?: () => void
  highlighted?: boolean
}

export const ACTION_ICONS: Record<number, React.FunctionComponent<React.SVGProps<SVGSVGElement>>> = {
  [ActionType.WAITING]: SandClockSvg,
  [ActionType.PARKING]: ParkingSvg,
  [ActionType.WASHING]: WashingSvg,
  [ActionType.CHARGING]: ChargingSvg,
  [ActionType.REFILLING]: RefillingSvg,
  [ActionType.WASTE_WATER_DRAIN]: WasteWaterDrainSvg,
  [ActionType.DEBRIS_DUMP]: DebrisDumpSvg,
}

const MOBILE_ACTION_CHIP_OUTER = 28
const MOBILE_ACTION_CHIP_GAP = 8
const MOBILE_ACTION_MENU_GAP = 6
const MOBILE_CHOOSE_ACTIONS_LABEL_WIDTH = 54

function getMaintenanceMobileActionColWidth(allowedActions?: ActionType[], needsChooseLabel = false): string {
  if (!allowedActions || allowedActions.length <= 1) {
    return '35px'
  }
  const chipsWidth =
    allowedActions.length * MOBILE_ACTION_CHIP_OUTER +
    (allowedActions.length - 1) * MOBILE_ACTION_CHIP_GAP +
    MOBILE_ACTION_MENU_GAP
  const labelWidth = needsChooseLabel ? MOBILE_CHOOSE_ACTIONS_LABEL_WIDTH + 4 : 0
  return `${chipsWidth + labelWidth}px`
}

export const MaintenanceItem = ({
  type,
  icon: Icon,
  hideDragging = false,
  hideActions = false,
  showEquipment = true,
  showLocalization = true,
  hideIdColumn = false,
  onDelete,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  isDragging,
  isFloating,
  description,
  time,
  onTimeChange,
  onEdit,
  dragIndicator = 'none',
  allowedActions,
  allowSingleActionChips = false,
  selectedActions = [],
  onActionToggle,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  onClick,
  highlighted,
}: TMaintenanceItem) => {
  const isMobile = useIsMobile()
  const actionChipsAllowedActions =
    allowedActions &&
    onActionToggle &&
    (allowedActions.length > 1 || (allowSingleActionChips && allowedActions.length === 1))
      ? allowedActions
      : undefined
  const showActionChips = actionChipsAllowedActions !== undefined
  const hasMultiActions = showActionChips
  const needsActionSelection =
    actionChipsAllowedActions !== undefined && actionChipsAllowedActions.length > 1 && selectedActions.length === 0
  const mobileActionColWidth = isMobile ? getMaintenanceMobileActionColWidth(allowedActions, hasMultiActions) : '35px'

  const gridStyle = {
    '--missions-table-grid': isMobile
      ? `${hideDragging ? '' : '5px '}${hideIdColumn ? '' : '40px '}1fr ${showLocalization ? '50px ' : ''}${
          showEquipment ? '35px ' : ''
        }${mobileActionColWidth}${hideActions ? '' : ' 35px'}`
      : `${hideDragging ? '' : '5px '}${hideIdColumn ? '' : '48px '}3fr ${showLocalization ? '1.5fr ' : ''}${
          showEquipment ? '1.5fr ' : ''
        }0.5fr${hideActions ? '' : ' 22px'}`,
  } as React.CSSProperties

  return (
    <div
      className={clsx(style.missionRow, {
        [style.isDragging]: isDragging,
        [style.missionRowFloating]: isFloating,
        [style.dragIndicatorTop]: dragIndicator === 'top',
        [style.dragIndicatorBottom]: dragIndicator === 'bottom',
        [style.highlighted]: highlighted,
      })}
      style={{
        ...gridStyle,
        cursor: onClick ? 'pointer' : hideDragging ? 'default' : 'grab',
        ...(needsActionSelection && !highlighted ? { backgroundColor: 'rgba(255, 77, 79, 0.15)' } : {}),
      }}
      title={needsActionSelection ? 'Service points have no selected actions' : undefined}
      draggable={!hideDragging}
      onClick={onClick}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
    >
      {!hideDragging && (
        <div className={style.headerColCentered} style={{ cursor: 'grab' }}>
          <span></span>⠿
        </div>
      )}
      {!hideIdColumn && (
        <div className={style.headerCol}>
          <Icon width={18} height={18} className={style.themedIcon} />
        </div>
      )}
      <div className={style.colLeftMultiline}>
        <div className={style.maintenanceNameLine}>
          {hideIdColumn ? <Icon width={18} height={18} className={style.themedIcon} /> : null}
          <span className={style.maintenanceNameText}>{type}</span>
        </div>
        {description && <div className={style.maintenanceDescription}>{description}</div>}
      </div>
      {showLocalization && <div className={style.headerColCentered}></div>}
      {showEquipment && <div className={style.headerColCentered}></div>}
      <div
        className={clsx(
          style.headerColCentered,
          hasMultiActions && (isMobile ? style.actionChipsColMobile : style.actionChipsCol)
        )}
        onClick={e => e.stopPropagation()}
      >
        {onTimeChange && time !== undefined ? (
          <Dropdown
            options={[
              { value: 10, text: '10s' },
              { value: 30, text: '30s' },
              { value: 60, text: '60s' },
              { value: 90, text: '90s' },
              { value: 120, text: '120s' },
              { value: 180, text: '180s' },
              { value: 300, text: '300s' },
              { value: 600, text: '600s' },
            ]}
            currentOption={{ value: time, text: `${time}s` }}
            onClickOption={opt => onTimeChange(Number(opt.value))}
            useSmallDropdownStyle={true}
            noShadow={true}
            minWidth="80px"
          />
        ) : actionChipsAllowedActions ? (
          <div className={style.actionChipsRow}>
            <ChooseActionsLabel
              className={clsx(
                style.chooseActionsBesideChips,
                !needsActionSelection && style.chooseActionsBesideChipsHidden
              )}
              aria-hidden={!needsActionSelection}
            />
            {actionChipsAllowedActions.map(action => {
              const ActionIcon = ACTION_ICONS[action] || SandClockSvg
              const isSelected = selectedActions.includes(action)
              return (
                <div
                  key={action}
                  className={style.actionChip}
                  data-selected={isSelected}
                  onClick={() => onActionToggle?.(action)}
                  style={{ cursor: onActionToggle ? 'pointer' : 'default' }}
                  title={ACTION_TYPE_LABELS[action] || ActionType[action]}
                >
                  <ActionIcon width={20} height={20} className={style.themedIcon} data-active={isSelected} />
                </div>
              )
            })}
          </div>
        ) : null}
      </div>
      {!hideActions && (
        <div className={style.headerCol} onClick={e => e.stopPropagation()}>
          {(onDelete || onEdit || onMoveUp || onMoveDown) && (
            <ActionsMenu>
              {!isFirst && onMoveUp && (
                <ActionsMenuItem onClick={onMoveUp}>
                  <span className={style.menuItemIcon} />
                  <span className={style.menuItemText}>Move Up</span>
                </ActionsMenuItem>
              )}
              {!isLast && onMoveDown && (
                <ActionsMenuItem onClick={onMoveDown}>
                  <span className={style.menuItemIcon} />
                  <span className={style.menuItemText}>Move Down</span>
                </ActionsMenuItem>
              )}
              {onEdit && (
                <ActionsMenuItem onClick={onEdit}>
                  <span className={style.menuItemIcon} />
                  <span className={style.menuItemText}>Edit</span>
                </ActionsMenuItem>
              )}
              {onDelete && (
                <ActionsMenuItem onClick={onDelete}>
                  <RemoveSvg className={style.menuItemIcon} />
                  <span className={style.menuItemText}>Exclude</span>
                </ActionsMenuItem>
              )}
            </ActionsMenu>
          )}
        </div>
      )}
    </div>
  )
}

export type TMissionsTable = {
  children: React.ReactNode
  hideActions?: boolean
  hideDragging?: boolean
  showEquipment?: boolean
  showLocalization?: boolean
  allChecked?: boolean
  onToggleAll?: (checked: boolean) => void
  showLightsToggle?: boolean
  allLightsOn?: boolean
  onToggleAllLights?: (on: boolean) => void
  hideIdColumn?: boolean
  onTableDragOver?: (e: React.DragEvent<HTMLDivElement>) => void
  onTableDrop?: (e: React.DragEvent<HTMLDivElement>) => void
}

export const MissionsTable = ({
  children,
  hideActions = false,
  hideDragging = false,
  showEquipment = true,
  showLocalization = true,
  allChecked = false,
  onToggleAll,
  showLightsToggle = false,
  allLightsOn = false,
  onToggleAllLights,
  hideIdColumn = false,
  onTableDragOver,
  onTableDrop,
}: TMissionsTable) => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()

  const gridStyle = {
    '--missions-table-grid': isMobile
      ? `${hideDragging ? '' : '5px '}${hideIdColumn ? '' : '40px '}1fr ${showLocalization ? '50px ' : ''}${
          showEquipment ? '35px ' : ''
        }35px${hideActions ? '' : ' 35px'}`
      : `${hideDragging ? '' : '5px '}${hideIdColumn ? '' : '48px '}3fr ${showLocalization ? '1.5fr ' : ''}${
          showEquipment ? '1.5fr ' : ''
        }0.5fr${hideActions ? '' : ' 22px'}`,
  } as React.CSSProperties

  return (
    <div
      style={{ width: '100%', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
      onDragOver={onTableDragOver}
      onDrop={onTableDrop}
    >
      <div className={style.missionsTableHeader} style={gridStyle}>
        {!hideDragging && <div className={style.headerColCentered}></div>}
        {!hideIdColumn && <div className={clsx(style.headerCol, 'text-16-400')}>{t('missions.id')}</div>}
        <div className={clsx(style.headerCol, 'text-16-400')}>{t('missions.name')}</div>
        {showLocalization && (
          <div className={clsx(style.headerColCentered, 'text-16-400')}>
            {isMobile ? (
              <LocationSvg width={18} height={18} className={style.themedIcon} />
            ) : (
              t('missions.localization')
            )}
          </div>
        )}
        {showEquipment && (
          <div className={clsx(style.headerColCentered, 'text-16-400')} style={{ cursor: 'pointer' }}>
            {showLightsToggle ? (
              <div
                onClick={() => onToggleAllLights?.(!allLightsOn)}
                style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}
              >
                {allLightsOn ? (
                  <HeadlightOnSvg width={24} height={24} className={style.themedIcon} data-active={true} />
                ) : (
                  <HeadlightOffSvg width={24} height={24} className={style.themedIcon} />
                )}
              </div>
            ) : (
              <HeadlightOnSvg width={24} height={24} className={style.themedIcon} data-active={true} />
            )}
          </div>
        )}
        <div className={clsx(style.headerColCentered, 'text-16-400')}>
          <div
            role="button"
            tabIndex={0}
            aria-pressed={allChecked}
            aria-label={
              allChecked
                ? t('missions.hide_all_on_map', 'Hide all on map')
                : t('missions.show_all_on_map', 'Show all on map')
            }
            title={
              allChecked
                ? t('missions.hide_all_on_map', 'Hide all on map')
                : t('missions.show_all_on_map', 'Show all on map')
            }
            onClick={() => onToggleAll?.(!allChecked)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onToggleAll?.(!allChecked)
              }
            }}
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            {allChecked ? (
              <VisibilityIcon fontSize="small" className={style.themedIcon} data-active={true} />
            ) : (
              <VisibilityOutlinedIcon fontSize="small" className={style.themedIcon} />
            )}
          </div>
        </div>
        {!hideActions && <div className={style.headerCol}></div>}
      </div>
      <div className={style.table}>{children}</div>
    </div>
  )
}
